/**
 * 利用申請（ログイン不要・審査制）。developer-console / coaches の `/apply` と同じ項目・同じ検証・同じ送り先
 * （`@kalori/business/applications/client`）。送り先は同じ worker の `POST /api/apply` → kalori-api の内部面。
 *
 * LP（kalori.jp/business/brands/）の CTA は `?s=<LP の訪問 ID>&via=<CTA 名>` を付けて来るので、表示・送信の計測で
 * 引き継ぐ（LP → 申請画面 → 送信を 1 つの session_id で追う）。
 * ⚠️ honeypot の欄（`website_url`）は人には見えない。埋まっていたら受け手が黙って捨てる（bot 対策）。
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { B2B_APPLY_QUESTIONS } from "@kalori/business/applications/client";
import { BRANDS_TERMS_URL } from "@kalori/business/brands";
import { Link } from "../router";
import { OnboardingLayout, FieldLabel, ApplyQuestion } from "./Onboarding";

const QUESTIONS = B2B_APPLY_QUESTIONS.merchant;
const PRIVACY_URL = "https://kalori.jp/ja/privacy/";

interface Values {
  email: string;
  org_name: string;
  contact_name: string;
  website: string;
  use_case: string;
  attrs: Record<string, string>;
  agree: boolean;
  website_url: string;
}

const EMPTY: Values = { email: "", org_name: "", contact_name: "", website: "", use_case: "", attrs: {}, agree: false, website_url: "" };

async function post(path: string, body: unknown): Promise<{ ok: boolean; status: number; json: Record<string, unknown> | null }> {
  try {
    const res = await fetch(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    return { ok: res.ok, status: res.status, json: (await res.json().catch(() => null)) as Record<string, unknown> | null };
  } catch {
    return { ok: false, status: 0, json: null };
  }
}

export function Apply({ query }: { query: URLSearchParams }) {
  const [values, setValues] = useState<Values>(EMPTY);
  const [tracking, setTracking] = useState<{ sid: string; via: string }>({ sid: "", via: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const errorRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    // 長いフォームでも、送信後のエラーが画面外に残らないようにする。
    if (error) errorRef.current?.focus();
  }, [error]);

  useEffect(() => {
    // 表示の計測と訪問 ID の決定（LP から来たら LP の ID を引き継ぐ）
    void post("/api/apply/view", { s: query.get("s"), via: query.get("via") }).then((r) => {
      const data = r.json?.data as { sid?: string; via?: string } | undefined;
      if (data?.sid) setTracking({ sid: data.sid, via: data.via ?? "" });
    });
  }, [query]);

  const set = (k: keyof Values, v: string | boolean) => setValues({ ...values, [k]: v });
  const setAttr = (k: string, v: string) => setValues({ ...values, attrs: { ...values.attrs, [k]: v } });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const r = await post("/api/apply", {
      email: values.email,
      org_name: values.org_name,
      contact_name: values.contact_name,
      website: values.website,
      use_case: values.use_case,
      ...values.attrs,
      agree: values.agree,
      website_url: values.website_url,
      sid: tracking.sid,
      via: tracking.via,
    });
    setBusy(false);
    if (r.ok && r.json?.success) return setDone(true);
    setError(typeof r.json?.error === "string" ? r.json.error : "申請を送れませんでした。しばらくしてからもう一度お試しください。");
  };

  if (done) {
    return (
      <OnboardingLayout title="申請を受け付けました">
        <div className="b2b-stack">
          <p className="b2b-lead">内容を確認のうえ、通常 2 営業日以内に {values.email} へご連絡します。承認後、管理画面への招待をお送りします。</p>
          <p className="b2b-note">届かない場合は迷惑メールのフォルダもご確認ください。</p>
          <p className="b2b-note"><a href="https://kalori.jp/ja/business/brands/">飲食ブランド向けのご案内へ戻る</a></p>
        </div>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout title="利用申請">
      <p className="b2b-lead">kalori に掲載中のメニューに、御社の公式の栄養成分値を提供いただくための管理画面です。運営が内容を確認し、承認後に招待をお送りします。まだ掲載されていないブランドの掲載のご希望も、こちらから受け付けます。現在は招待制のパイロットです。公式値の掲載と、掲載後の誤りの修正に費用はかかりません。今後追加する機能は有料になる場合があります。</p>
      <form className="b2b-form b2b-stack" onSubmit={submit}>
        {error && <p className="b2b-error" role="alert" tabIndex={-1} ref={errorRef}>{error}</p>}
        <section className="b2b-group">
          <h2>事業者情報</h2>
          <div className="b2b-stack">
            <label className="b2b-field">
              <FieldLabel label="メールアドレス" required />
              <input type="email" required maxLength={254} autoComplete="email" value={values.email} onChange={(e) => set("email", e.target.value)} aria-describedby="b2b-email-hint" />
              <span className="b2b-hint b2b-hint-after" id="b2b-email-hint">ログインに使います。御社の公式サイトと同じドメインのアドレスだと、ブランドの確認が早く進みます。</span>
            </label>
            <label className="b2b-field">
              <FieldLabel label="会社名・ブランド名" required />
              <input required maxLength={100} autoComplete="organization" value={values.org_name} onChange={(e) => set("org_name", e.target.value)} placeholder="例: ◯◯フーズ / ◯◯食堂" />
            </label>
            <label className="b2b-field">
              <FieldLabel label="ご担当者名" />
              <input maxLength={100} autoComplete="name" value={values.contact_name} onChange={(e) => set("contact_name", e.target.value)} />
            </label>
            <label className="b2b-field">
              <FieldLabel label="公式サイト" />
              <input type="url" maxLength={200} value={values.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" />
            </label>
          </div>
        </section>
        <section className="b2b-group">
          <h2>ご相談の内容</h2>
          <div className="b2b-stack">
            {QUESTIONS.map((q) => <ApplyQuestion key={q.key} question={q} value={values.attrs[q.key] ?? ""} onChange={(v) => setAttr(q.key, v)} />)}
            <label className="b2b-field">
              <FieldLabel label="ご相談の詳細" />
              <textarea rows={5} maxLength={2000} value={values.use_case} onChange={(e) => set("use_case", e.target.value)} placeholder="例: kalori に載っている推定値を、公式の栄養成分値に差し替えたい（約 100 商品）／ まだ掲載されていないので掲載してほしい（公式サイトの URL）" />
            </label>
          </div>
        </section>
        {/* honeypot（人には見えない・スクリーンリーダーにも読ませない） */}
        <div className="b2b-hp" aria-hidden="true"><label>website_url<input tabIndex={-1} autoComplete="off" value={values.website_url} onChange={(e) => set("website_url", e.target.value)} /></label></div>
        <label className="b2b-agree">
          <input type="checkbox" required checked={values.agree} onChange={(e) => set("agree", e.target.checked)} />
          <span><a href={BRANDS_TERMS_URL} target="_blank" rel="noopener noreferrer">利用規約（飲食ブランド向け補遺）</a>と<a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>に同意します</span>
        </label>
        <button className="b2b-button" disabled={busy} aria-busy={busy}>{busy ? "送信しています…" : "申請する"}</button>
      </form>
      <p className="b2b-after">すでに招待を受け取った方は <Link href="/login">ログイン</Link>。</p>
    </OnboardingLayout>
  );
}
